import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import { ChartSkeleton } from "../../Components/Accounting/SkeletonLoader";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import Select2 from "../../Components/Accounting/Select2";
import ReactApexChart from "react-apexcharts";
import toast, { Toaster } from "react-hot-toast";
import AuthContext from "../../Context/AuthContext";
import useOrgProfile from "../../util/accounting/orgProfile";
import { CHARTS, dashboardFor } from "./dashboardConfig";
import ExportBar from "../../Components/Accounting/ExportBar";

/**
 * Industry-aware dashboard, laid out in four bands:
 *
 *   1. three hero figures, each with its month/week movement and a sparkline
 *   2. financial position · where the money comes from · what just happened
 *   3. the charts that matter to this industry
 *   4. the documents to open next, then quick access
 *
 * Which figures, charts and links appear is decided by dashboardConfig.js from
 * the company's industry, and the whole payload arrives in a single request
 * (accounting/dashboard/overview) rather than the nine this page used to fire.
 */

const compact = (v) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + "B";
  if (abs >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (abs >= 1_000) return (n / 1_000).toFixed(0) + "K";
  return String(Math.round(n));
};

/** Walk a ["charts","rve","revenue"] path into the payload. */
const at = (obj, path) => (path || []).reduce((o, k) => (o == null ? o : o[k]), obj);

const STATUS_TONE = {
  paid: "ok", posted: "ok", approved: "ok", active: "ok",
  partial: "warn", sent: "warn", pending: "warn", unpaid: "warn",
  overdue: "bad", void: "muted", draft: "muted", closed: "muted",
};

const AccountingDashboard = () => {
  const { industry } = useContext(AuthContext);
  const navigate = useNavigate();
  const org = useOrgProfile();
  const layout = useMemo(() => dashboardFor(industry), [industry]);

  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState("");
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const money = useMemo(
    () => (v) => `${org.currency} ${(Number(v) || 0).toLocaleString()}`,
    [org.currency]
  );

  useEffect(() => { fetchFinancialYears(); }, []);
  useEffect(() => { if (selectedFY) fetchOverview(selectedFY); }, [selectedFY, industry]); // eslint-disable-line

  const fetchFinancialYears = async () => {
    const res = await ajaxAccounting.listFinancialYears({});
    if (res.status !== "OK") { setLoading(false); return; }
    const years = res.details || [];
    setFinancialYears(years);
    // acc_financial_years has no is_current column — the open year is current.
    const current = years.find((y) => y.status === "open") || years[0];
    if (current) setSelectedFY(String(current.id));
    else setLoading(false);
  };

  const fetchOverview = async (fyId) => {
    setLoading(true);
    const res = await ajaxAccounting.getDashboardOverview({
      financial_year_id: parseInt(fyId, 10),
      industry,
    });
    if (res.status === "OK") setOverview(res.details);
    else toast.error(res.message || "Could not load the dashboard.");
    setLoading(false);
  };

  const summary = overview?.summary || {};
  const periods = overview?.periods || {};
  const ranked = overview?.ranked || { items: [] };
  const activity = overview?.activity || [];
  const documents = overview?.documents || { columns: [], rows: [] };
  const hasDocuments = (documents.rows?.length ?? 0) > 0;
  const currentFY = overview?.financial_year || financialYears.find((y) => String(y.id) === selectedFY);
  const fyOptions = financialYears.map((y) => ({
    value: String(y.id),
    label: y.name + (y.status === "open" ? " (Open)" : ""),
  }));

  const fmt = (card) => {
    const v = summary[card.key];
    if (card.type === "count") return (v ?? 0).toLocaleString();
    if (card.type === "percent") return `${v ?? 0}%`;
    return functions.formatCurrency(v, org.currency);
  };

  const kpiExportRows = layout.kpis.map((c) => ({ metric: c.label, value: fmt(c) }));

  // Year-to-date split behind the "Income vs spend" bar. Overspend is clamped
  // to a full bar so a loss reads as "all of it and then some", not overflow.
  const revenueYtd = Number(summary.total_revenue) || 0;
  const expensesYtd = Number(summary.total_expenses) || 0;
  const profitYtd = revenueYtd - expensesYtd;
  const spendShare = revenueYtd > 0 ? Math.min(100, (expensesYtd / revenueYtd) * 100) : 0;

  // ── Chart option builders ─────────────────────────────────────────────────
  const base = (extra = {}) => ({
    chart: { toolbar: { show: false }, fontFamily: "inherit", ...extra.chart },
    dataLabels: { enabled: false },
    grid: { borderColor: "rgba(0,0,0,0.06)" },
    ...extra,
  });

  const optionsFor = (spec, data) => {
    const categories = at(data, [spec.categories]) || [];
    const tip = spec.money === false ? (v) => v : money;
    switch (spec.kind) {
      case "donut":
        return {
          chart: { type: "donut", fontFamily: "inherit" },
          labels: categories,
          colors: ["#10b981", "#0ea5e9", "#6366f1", "#f59e0b", "#ec4899", "#14b8a6", "#8b5cf6", "#ef4444"],
          legend: { position: "bottom" },
          dataLabels: { enabled: true, formatter: (v) => v.toFixed(1) + "%" },
          tooltip: { y: { formatter: tip } },
          plotOptions: { pie: { donut: { size: "68%" } } },
          stroke: { width: 0 },
        };
      case "area":
        return base({
          chart: { type: "area", toolbar: { show: false }, fontFamily: "inherit" },
          stroke: { curve: "smooth", width: 2 },
          colors: spec.series.map((s) => s[2]),
          fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.02 } },
          xaxis: { categories },
          yaxis: { labels: { formatter: compact } },
          tooltip: { y: { formatter: tip } },
        });
      case "hbar":
        return base({
          chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
          plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "58%" } },
          colors: spec.series.map((s) => s[2]),
          xaxis: { categories, labels: { formatter: compact } },
          tooltip: { y: { formatter: tip } },
        });
      default:
        return base({
          chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit", stacked: !!spec.stacked },
          plotOptions: { bar: { columnWidth: "48%", borderRadius: 4 } },
          colors: spec.series.map((s) => s[2]),
          xaxis: { categories },
          yaxis: { labels: { formatter: compact } },
          legend: { position: "top", horizontalAlign: "left", markers: { radius: 4 } },
          tooltip: { y: { formatter: tip } },
        });
    }
  };

  const seriesFor = (spec, data) =>
    spec.kind === "donut"
      ? at(data, [spec.valueKey]) || []
      : spec.series.map(([name, key]) => ({ name, data: at(data, [key]) || [] }));

  const hasData = (spec, data) => {
    if (!data) return false;
    if (spec.kind === "donut") return (at(data, [spec.valueKey]) || []).some((v) => Number(v) !== 0);
    return spec.series.some(([, key]) => (at(data, [key]) || []).some((v) => Number(v) !== 0));
  };

  const renderChart = ({ id, col }) => {
    const spec = CHARTS[id];
    if (!spec) return null;
    const data = at(overview, [spec.source === "extra" ? "extra" : "charts", spec.id]);
    const ready = hasData(spec, data);
    return (
      <div className={`${col} mb-3`} key={id}>
        <div className="chart-card">
          <div className="chart-card-title">{spec.title}</div>
          {loading ? <ChartSkeleton height="270px" /> : ready ? (
            <ReactApexChart
              options={optionsFor(spec, data)}
              series={seriesFor(spec, data)}
              type={spec.kind === "hbar" ? "bar" : spec.kind}
              height={spec.kind === "donut" ? 290 : 270}
            />
          ) : (
            <div className="panel-empty">No data for this period</div>
          )}
        </div>
      </div>
    );
  };

  // ── Hero tiles ────────────────────────────────────────────────────────────
  const sparkOptions = (tone) => ({
    chart: { type: "bar", sparkline: { enabled: true }, fontFamily: "inherit" },
    plotOptions: { bar: { columnWidth: "58%", borderRadius: 2 } },
    colors: [tone === "down" ? "#f2b8b5" : tone === "up" ? "#9adcc0" : "#bcc7dc"],
    tooltip: { enabled: false },
  });

  // Colours here match the .rank-fill tones so the ring and the bars below it
  // read as one card rather than two unrelated views of the same numbers.
  const rankDonutOptions = useMemo(
    () => ({
      chart: { type: "donut", fontFamily: "inherit" },
      labels: ranked.items?.map((r) => r.name) || [],
      colors: ["#12b76a", "#f79009", "#6172f3", "#ee46bc", "#06aed4", "#98a2b3"],
      legend: { show: false },
      dataLabels: { enabled: false },
      stroke: { width: 0 },
      plotOptions: {
        pie: {
          donut: {
            size: "74%",
            labels: {
              show: true,
              name: { show: false },
              total: {
                show: true,
                showAlways: true,
                label: "Total",
                fontSize: "11px",
                color: "#98a2b3",
                formatter: () => compact(ranked.total),
              },
              value: { fontSize: "16px", fontWeight: 600, color: "#101828", formatter: compact },
            },
          },
        },
      },
      tooltip: { y: { formatter: money } },
    }),
    [ranked.items, ranked.total, money]
  );

  // Cash on hand against money owed to us and money we owe. Distributed
  // colours because each bar is its own category, not a series.
  const liquiditySeries = useMemo(
    () => [{
      name: "Amount",
      data: [
        Number(summary.cash_balance) || 0,
        Number(summary.outstanding_ar) || 0,
        Number(summary.outstanding_ap) || 0,
      ],
    }],
    [summary.cash_balance, summary.outstanding_ar, summary.outstanding_ap]
  );

  const liquidityOptions = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
      plotOptions: { bar: { horizontal: true, distributed: true, borderRadius: 4, barHeight: "52%" } },
      colors: ["#06aed4", "#12b76a", "#f79009"],
      dataLabels: {
        enabled: true,
        formatter: compact,
        offsetX: 26,
        style: { fontSize: "11px", fontWeight: 600, colors: ["#475467"] },
      },
      legend: { show: false },
      grid: { borderColor: "rgba(0,0,0,0.05)", padding: { left: 0, right: 12 } },
      xaxis: {
        categories: ["Cash & bank", "Owed to us", "We owe"],
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: { fontSize: "11px", colors: "#667085" } } },
      tooltip: { y: { formatter: money } },
    }),
    [money]
  );

  const renderHero = (hero) => {
    const p = periods[hero.period] || {};
    const spark = (at(overview, hero.spark) || []).map((v) => Number(v) || 0);
    const value = summary[hero.key];
    const negative = typeof value === "number" && value < 0;
    const delta = p.delta;
    return (
      <div className="col-lg-4 mb-3" key={hero.key}>
        <div className="hero-card">
          <div className="hero-card-label">{hero.label}</div>
          <div className="hero-card-head">
            <div className={`hero-card-value${negative ? " is-negative" : ""}`}>
              {functions.formatCurrency(value, org.currency)}
            </div>
            {delta !== null && delta !== undefined && (
              <span className={`hero-delta ${delta >= 0 ? "up" : "down"}`}>
                <i className={`fa-solid fa-arrow-${delta >= 0 ? "up" : "down"}`} aria-hidden="true"></i>
                {Math.abs(delta)}%
              </span>
            )}
          </div>
          <div className="hero-card-foot">
            {hero.period ? (
              <div className="hero-periods">
                <div>
                  <div className="hero-period-label">This month</div>
                  <div className="hero-period-value">{functions.formatCurrency(p.month ?? 0, org.currency)}</div>
                </div>
                <div>
                  <div className="hero-period-label">This week</div>
                  <div className="hero-period-value">{functions.formatCurrency(p.week ?? 0, org.currency)}</div>
                </div>
              </div>
            ) : <div className="hero-periods" />}
            {spark.some((v) => v !== 0) && (
              <div className="hero-spark">
                <ReactApexChart options={sparkOptions(hero.tone)} series={[{ data: spark }]} type="bar" height={46} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <AppContainer
      title={layout.label}
      previousPageTitle={org.name}
      description={layout.blurb}
      actions={[
        { label: "New Journal", icon: "fa-solid fa-plus", className: "btn btn-primary", onClick: () => { navigate("/accounting/journal/new"); } },
        { label: "New Invoice", icon: "fa-solid fa-file-invoice", className: "btn btn-outline-primary", onClick: () => { navigate("/accounting/invoices/new?type=outgoing"); } },
      ]}
    >
      <Toaster />

      <div className="dash-toolbar mb-3">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div style={{ minWidth: "230px" }}>
            <Select2 options={fyOptions} value={selectedFY} onChange={setSelectedFY} placeholder="Financial Year" />
          </div>
          {currentFY ? (
            <span className="tx-12 tx-muted">
              {functions.formatDate(currentFY.start_date)} — {functions.formatDate(currentFY.end_date)}
              <span className={`status-pill ms-2 ${currentFY.status === "open" ? "ok" : "muted"}`}>
                {currentFY.status === "open" ? "Open" : "Closed"}
              </span>
            </span>
          ) : (
            <span className="tx-12 tx-muted">
              No financial year yet. <Link to="/accounting/financial-year">Create one →</Link>
            </span>
          )}
        </div>
        <ExportBar
          className="no-print"
          data={kpiExportRows}
          columns={[{ key: "metric", label: "Metric" }, { key: "value", label: "Value", numeric: true }]}
          filename={`dashboard-${(currentFY?.name || "current").replace(/\s+/g, "-").toLowerCase()}`}
          pdfTitle={layout.label}
          subtitle={currentFY ? `Financial Year: ${currentFY.name}` : undefined}
          landscape={false}
        />
      </div>

      {/* 1 — hero figures */}
      <div className="row">
        {loading
          ? [0, 1, 2].map((i) => (
              <div className="col-lg-4 mb-3" key={i}><div className="hero-card is-loading" /></div>
            ))
          : layout.heroes.map(renderHero)}
      </div>

      {/* 2 — position · sources · activity */}
      <div className="row">
        <div className="col-lg-4 mb-3">
          <div className="panel h-100">
            <div className="panel-head"><span className="panel-title">Financial position</span></div>
            <div className="figure-grid">
              {layout.overview.map((c) => (
                <div className="figure" key={c.key}>
                  <div className="figure-label">
                    {c.label}
                    {/* A figure that names a list links straight to it — a count
                        of debtors is only useful if you can see who they are. */}
                    {c.link && (
                      <Link className="figure-link" to={c.link}>{c.linkLabel || "View"}</Link>
                    )}
                  </div>
                  <div className="figure-value">{fmt(c)}</div>
                  {c.sub && (
                    <div className="figure-sub">{fmt({ ...c.sub, type: c.sub.type })}</div>
                  )}
                </div>
              ))}
            </div>
            {/* Can we pay what we owe? The figures above state cash, debtors and
                creditors separately; only side by side do they answer that. */}
            <div className="position-chart">
              <div className="split-title">Liquidity</div>
              <ReactApexChart options={liquidityOptions} series={liquiditySeries} type="bar" height={158} />
            </div>

            {/* What the year's income was consumed by, in one bar — the
                question the figures above imply but never quite answer. */}
            <div className="split">
              <div className="split-head">
                <span className="split-title">Income vs spend</span>
                <span className="split-total">{functions.formatCurrency(revenueYtd, org.currency)}</span>
              </div>
              {revenueYtd > 0 ? (
                <>
                  <div className="split-bar">
                    <div className="split-seg spend" style={{ width: `${spendShare}%` }} />
                    <div className={`split-seg ${profitYtd < 0 ? "loss" : "profit"}`} style={{ width: `${100 - spendShare}%` }} />
                  </div>
                  <div className="split-legend">
                    <span><i className="dot spend" />Spent {Math.round(spendShare)}% · {functions.formatCurrency(expensesYtd, org.currency)}</span>
                    <span>
                      <i className={`dot ${profitYtd < 0 ? "loss" : "profit"}`} />
                      {profitYtd < 0 ? "Shortfall" : "Retained"} {functions.formatCurrency(Math.abs(profitYtd), org.currency)}
                    </span>
                  </div>
                </>
              ) : (
                <div className="split-empty">No income recorded for this period yet.</div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-4 mb-3">
          <div className="panel h-100">
            <div className="panel-head">
              <span className="panel-title">{ranked.label || "Revenue by source"}</span>
              <span className="tx-12 tx-muted">{functions.formatCurrency(ranked.total, org.currency)}</span>
            </div>
            {ranked.items?.length ? (
              <>
                {/* The share picture and the exact figures in one card — this
                    used to be a separate "Revenue Breakdown" donut showing the
                    same numbers over again. */}
                <div className="rank-donut">
                  <ReactApexChart
                    options={rankDonutOptions}
                    series={ranked.items.map((r) => r.value)}
                    type="donut"
                    height={168}
                  />
                </div>
                <div className="rank-list">
                {ranked.items.map((r, i) => (
                  // Rows that carry a link drill down (school: class group -> streams).
                  <div
                    className={`rank-row${r.link ? " is-link" : ""}`}
                    key={r.name}
                    role={r.link ? "link" : undefined}
                    tabIndex={r.link ? 0 : undefined}
                    title={r.link ? `View ${r.name} by stream` : r.name}
                    onClick={r.link ? () => navigate(r.link) : undefined}
                    onKeyDown={r.link ? (e) => { if (e.key === "Enter") navigate(r.link); } : undefined}
                  >
                    <div className="rank-row-head">
                      <span className="rank-name" title={r.name}>
                        {r.name}
                        {r.link && <i className="fa-solid fa-arrow-right rank-go" aria-hidden="true"></i>}
                      </span>
                      <span className="rank-pct">{r.pct}%</span>
                    </div>
                    <div className="rank-track">
                      <div className={`rank-fill tone-${i % 5}`} style={{ width: `${Math.max(2, r.pct)}%` }} />
                    </div>
                    <div className="rank-value">{functions.formatCurrency(r.value, org.currency)}</div>
                  </div>
                ))}
                </div>
              </>
            ) : (
              <div className="panel-empty">Nothing recorded for this period</div>
            )}
          </div>
        </div>

        <div className="col-lg-4 mb-3">
          <div className="panel h-100">
            <div className="panel-head">
              <span className="panel-title">Recent activity</span>
              <Link className="panel-link" to="/accounting/journal">View all</Link>
            </div>
            {activity.length ? (
              <ul className="feed">
                {activity.map((a, i) => (
                  <li className="feed-item" key={`${a.reference}-${i}`}>
                    <span className={`feed-dot ${STATUS_TONE[a.status] || "muted"}`} aria-hidden="true" />
                    <div className="feed-body">
                      <div className="feed-title" title={a.description}>{a.description || a.reference}</div>
                      <div className="feed-meta">{functions.formatDate(a.date)} · {a.reference}</div>
                    </div>
                    <div className="feed-amount">{functions.formatCurrency(a.amount, org.currency)}</div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="panel-empty">No entries posted yet</div>
            )}
          </div>
        </div>
      </div>

      {/* 3 — the charts for this industry */}
      <div className="row">{layout.charts.map(renderChart)}</div>

      {/* 4 — the paired chart and the documents to open next, side by side */}
      <div className="row">
        {layout.pairedChart && renderChart({
          id: layout.pairedChart,
          col: hasDocuments ? "col-lg-6" : "col-lg-12",
        })}
        {hasDocuments && (
          <div className={`${layout.pairedChart ? "col-lg-6" : "col-12"} mb-3`}>
            <div className="panel h-100">
              <div className="panel-head">
                <span className="panel-title">{documents.label}</span>
                {documents.link && <Link className="panel-link" to={documents.link}>View all</Link>}
              </div>
              <div className="table-responsive">
                <table className="table doc-table mb-0">
                  <thead>
                    <tr>
                      {documents.columns.map((c) => (
                        <th key={c.key} className={c.numeric ? "text-end" : ""}>{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {documents.rows.map((row, i) => (
                      <tr key={`${row.reference}-${i}`}>
                        {documents.columns.map((c) => (
                          <td key={c.key} className={c.numeric ? "text-end" : ""}>
                            {c.key === "status" ? (
                              <span className={`status-pill ${STATUS_TONE[row[c.key]] || "muted"}`}>{row[c.key]}</span>
                            ) : c.key === "date" ? (
                              functions.formatDate(row[c.key])
                            ) : c.numeric ? (
                              functions.formatCurrency(row[c.key], org.currency)
                            ) : (
                              row[c.key] ?? "—"
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="row">
        <div className="col-12 mb-2"><h6 className="section-label mb-0">Quick access</h6></div>
        {layout.quickLinks.map((q) => (
          <div key={q.link} className="col-xl-3 col-lg-4 col-sm-6 mb-3">
            <Link to={q.link} className="quick-link">
              <span>{q.label}</span>
              <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
            </Link>
          </div>
        ))}
      </div>
    </AppContainer>
  );
};

export default AccountingDashboard;
